import { apiUrl } from "../taskpane";

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data?.detail;
    const error = new Error((typeof detail === "string" ? detail : detail?.message) || "Request failed");
    error.status = response.status;
    error.body = data;
    error.errors = detail?.errors || [];
    error.revision = detail?.revision;
    throw error;
  }
  return data;
};

const authHeaders = (token, json = false) => ({
  Authorization: `Bearer ${token}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

let buildingTypesCache = null;

/** All 635 building types ({ default, items: [{ code, level, parent, name_ka, name_en }] }), fetched once. */
export async function fetchBuildingTypes(token) {
  if (!buildingTypesCache) {
    buildingTypesCache = fetch(apiUrl("building-types"), { headers: authHeaders(token) })
      .then(parseResponse)
      .catch((err) => {
        buildingTypesCache = null;
        throw err;
      });
  }
  return buildingTypesCache;
}

/** { system: R04 template (tree, parking_tree, presets), saved: [{ id, name, scope, tree }] } */
export async function fetchStructureTemplates(token) {
  const res = await fetch(apiUrl("structure-templates"), { headers: authHeaders(token) });
  return parseResponse(res);
}

export async function saveStructureTemplate(token, { name, scope, tree }) {
  const res = await fetch(apiUrl("structure-templates"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ name, scope, tree }),
  });
  return parseResponse(res);
}

export async function deleteStructureTemplate(token, id) {
  const res = await fetch(apiUrl(`structure-templates/${id}`), { method: "DELETE", headers: authHeaders(token) });
  return parseResponse(res);
}

/** { exists, site, trees, settings, revision, updated_by, updated_at } */
export async function fetchProjectStructure(token, projectId) {
  const res = await fetch(apiUrl(`projects/${projectId}/structure`), { headers: authHeaders(token) });
  return parseResponse(res);
}

/** 409 (error.revision) when someone saved since `revision`; 422 with error.errors when invalid. */
export async function saveProjectStructure(token, projectId, { site, trees, settings, revision }) {
  const res = await fetch(apiUrl(`projects/${projectId}/structure`), {
    method: "PUT",
    headers: authHeaders(token, true),
    body: JSON.stringify({ site, trees, settings, revision }),
  });
  return parseResponse(res);
}

/** { revision, sheets: [{ object_id, node_id, rows }] } — content of every sheet still in the structure. */
export async function fetchStructureSheets(token, projectId) {
  const res = await fetch(apiUrl(`projects/${projectId}/structure/sheets`), { headers: authHeaders(token) });
  return parseResponse(res);
}

/** Replace sheet content; 409 (error.revision) when the structure changed since `revision`. */
export async function saveStructureSheets(token, projectId, { revision, sheets }) {
  const res = await fetch(apiUrl(`projects/${projectId}/structure/sheets`), {
    method: "PUT",
    headers: authHeaders(token, true),
    body: JSON.stringify({ revision, sheets }),
  });
  return parseResponse(res);
}

const buildingTypeRequest = async (token, path, method, body) => {
  const res = await fetch(apiUrl(path), { method, headers: authHeaders(token, !!body), body: body ? JSON.stringify(body) : undefined });
  const data = await parseResponse(res);
  buildingTypesCache = null;
  return data;
};

/** Superusers: a new type under `parent` (null = top level); the code is assigned by the server. */
export const createBuildingType = (token, { parent, name_ka, name_en }) => buildingTypeRequest(token, "building-types", "POST", { parent, name_ka, name_en });
export const updateBuildingType = (token, code, patch) => buildingTypeRequest(token, `building-types/${code}`, "PATCH", patch);
export const deleteBuildingType = (token, code) => buildingTypeRequest(token, `building-types/${code}`, "DELETE");
export const invalidateBuildingTypes = () => {
  buildingTypesCache = null;
};

/** Superusers: replace parts of the default template ({ tree, parking_tree, presets, name }). */
export async function saveSystemTemplate(token, body) {
  const res = await fetch(apiUrl("structure-templates/system"), { method: "PUT", headers: authHeaders(token, true), body: JSON.stringify(body) });
  return parseResponse(res);
}
